// Podgląd rejestru ośrodka na stronie badania: kilka najpilniejszych otwartych pozycji pod nagłówkiem ośrodka,
// żeby było je widać bez wchodzenia do rejestru. Tylko odczyt; kliknięcie prowadzi do rejestru.
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchResortIssues, type SiteIssue } from "../../api/siteIssuesApi";
import { ActionStatus } from "../../types/reportActions";
import { formatDay } from "./siteIssueRows";

export const PREVIEW_LIMIT = 5;

const isSerious = (i: SiteIssue) => i.serious || i.seriousBreach;

// Najpierw do weryfikacji, potem poważne, potem najstarsze (data identyfikacji, bez daty na końcu)
export const previewItems = (items: SiteIssue[]): SiteIssue[] =>
  items
    .filter((i) => i.status === ActionStatus.Open)
    .sort((a, b) =>
      Number(b.needsReview) - Number(a.needsReview) ||
      Number(isSerious(b)) - Number(isSerious(a)) ||
      (a.identificationDate ?? "9999").localeCompare(b.identificationDate ?? "9999")
    );

const Tag = ({ children, tone }: { children: string; tone: "red" | "amber" }) => (
  <span
    className={`inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-medium whitespace-nowrap ${
      tone === "red" ? "bg-[var(--sc-red-bg)] text-[var(--sc-red)]" : "bg-[var(--sc-amber-bg)] text-[#C07810]"
    }`}
  >
    {children}
  </span>
);

export const ResortIssuesPreview = ({ resortId, enabled = true }: { resortId: string; enabled?: boolean }) => {
  const navigate = useNavigate();
  const { data } = useQuery(["siteIssues", resortId], () => fetchResortIssues(resortId), {
    enabled, retry: false, staleTime: 60 * 1000,
  });
  const open = useMemo(() => previewItems(data?.items ?? []), [data]);
  if (!enabled || open.length === 0) return null;

  const registerUrl = `/resorts/${resortId}/issues`;
  const shown = open.slice(0, PREVIEW_LIMIT);
  // Po migracji numer uczestnika bywa tylko w opisie: pusta kolumna nie zajmuje miejsca
  const showParticipant = shown.some((i) => i.participantNumber?.trim());
  return (
    <div className="mb-2.5 rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-[var(--sc-border)] bg-[var(--sc-bg)]">
        <span className="text-[11px] font-medium text-[var(--sc-text)]">Otwarte w rejestrze ośrodka</span>
        <span className="text-[10px] text-[var(--sc-muted)]">
          {open.length > PREVIEW_LIMIT ? `najpilniejsze ${PREVIEW_LIMIT} z ${open.length}` : `${open.length}`}
        </span>
        <div className="flex-1" />
        <Link to={`${registerUrl}?filter=open`} className="text-[11px] text-[var(--sc-blue)] hover:underline whitespace-nowrap">
          {open.length > PREVIEW_LIMIT ? `Wszystkie otwarte (${open.length}) →` : "Otwórz rejestr →"}
        </Link>
      </div>
      <ul className="m-0 p-0 list-none">
        {shown.map((i) => (
          <li
            key={i.id}
            onClick={() => navigate(i.needsReview ? `${registerUrl}?filter=review` : `${registerUrl}?filter=open`)}
            title={[i.description, i.reviewNote && `Do weryfikacji: ${i.reviewNote}`].filter(Boolean).join("\n\n")}
            className="flex items-center gap-3 px-3.5 py-1.5 border-b border-[var(--sc-border)] last:border-b-0 cursor-pointer hover:bg-[var(--sc-bg)] transition-colors"
          >
            {showParticipant && (
              <span className="w-[72px] flex-shrink-0 text-[11px] font-medium text-[var(--sc-text)] truncate">
                {i.participantNumber || "—"}
              </span>
            )}
            <span className="w-[150px] flex-shrink-0 text-[10px] text-[var(--sc-muted)] uppercase tracking-[0.04em] truncate">
              {i.category ?? ""}
            </span>
            <span className="flex-1 min-w-0 text-[12px] text-[var(--sc-text)] truncate">{i.description}</span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {i.needsReview && <Tag tone="red">do weryfikacji</Tag>}
              {i.seriousBreach && <Tag tone="red">Serious Breach</Tag>}
              {i.serious && <Tag tone="amber">poważne</Tag>}
            </span>
            <span className="w-[70px] flex-shrink-0 text-right text-[11px] text-[var(--sc-muted)]">
              {formatDay(i.identificationDate)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
